import type { PostTablesLabels200DataItem } from '@ipfabric/api-client/client/schemas/postTablesLabels200DataItem';
import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import { Link } from '@ipfabric/ui-components/Link/Link';
import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import { createFrontendOnlyColumn } from 'components/Table/columns/frontendOnlyColumn';
import type { DiscoveryDataEnrichmentLink } from '../discoveryDataEnrichmentLink';
import { readAutoRuleLink, readTableCell } from './assignedLabelsRow';

const AUTO_RULE_TEXT = 'Auto-assignment rule';
const MANUAL_ASSIGNMENT_TEXT = 'Manual assignment';
const IPF_ASSIGNMENT_PREFIX = 'Hostname';
const REMOVED_RULE_TOOLTIP = 'The auto-assignment rule was removed';

/**
 * What produced a label assignment, derived from the root of its inheritance
 * chain. Inherited rows share the root of their chain, so they resolve to the
 * very same source as the assignment they were inherited from.
 */
export type AssignedBySource =
  | { kind: 'link'; text: string; link: DiscoveryDataEnrichmentLink }
  | { kind: 'removedRule'; text: string }
  | { kind: 'plain'; text: string | null };

export const resolveAssignedBySource = (
  row: PostTablesLabels200DataItem,
): AssignedBySource => {
  const assignedBy = readTableCell(row.assignedBy);
  const rootId = readTableCell(row.rootId);
  const rootAssignment = readTableCell(row.rootAssignment);

  if (rootId === undefined) {
    return { kind: 'plain', text: assignedBy ?? null };
  }

  if (rootAssignment === 'auto') {
    const text = assignedBy ?? AUTO_RULE_TEXT;
    const link = readAutoRuleLink(row);

    return link === undefined
      ? { kind: 'removedRule', text }
      : { kind: 'link', link, text };
  }

  if (rootAssignment === 'ipf') {
    return {
      kind: 'plain',
      text:
        assignedBy === undefined
          ? IPF_ASSIGNMENT_PREFIX
          : `${IPF_ASSIGNMENT_PREFIX} ${assignedBy}`,
    };
  }

  if (rootAssignment === 'manual') {
    const isInherited = readTableCell(row.labelId) !== rootId;

    return {
      kind: 'link',
      link: { tab: 'assignedLabels', rootId },
      text:
        isInherited && assignedBy !== undefined
          ? `${MANUAL_ASSIGNMENT_TEXT} on ${assignedBy}`
          : MANUAL_ASSIGNMENT_TEXT,
    };
  }

  return { kind: 'plain', text: assignedBy ?? null };
};

/** Names the page a source link opens, for places that already show the source. */
export const assignmentSourceLinkText = (
  link: DiscoveryDataEnrichmentLink,
): string =>
  link.tab === 'rules' ? 'Show in Label rules' : 'Show in Assigned labels';

type AssignedByColumnOptions = {
  hidden?: boolean;
  /**
   * Labels the link with the page it opens and points at it with an arrow,
   * instead of naming the source the row already shows in another column.
   */
  linkToPage?: boolean;
  /** Omitted when the user cannot reach the Discovery Data Enrichment page. */
  onOpen?: (link: DiscoveryDataEnrichmentLink) => void;
  title?: string;
};

export const createAssignedByColumn = ({
  hidden,
  linkToPage,
  onOpen,
  title = 'Assigned by',
}: AssignedByColumnOptions) =>
  createFrontendOnlyColumn<PostTablesLabels200DataItem>({
    key: 'assignedBy',
    title,
    hidden,
    webDependencies: [
      'assignedBy',
      'labelId',
      'rootAssignment',
      'rootId',
      'rootRuleId',
    ],
    csvStringDependencies: ['assignedBy'],
    toCsvString: (row) => readTableCell(row.assignedBy) ?? '',
    render: (row) => {
      const source = resolveAssignedBySource(row);

      if (source.kind === 'removedRule') {
        return (
          <span className='flex flex-row items-center gap-3xs text-disabled'>
            {source.text}
            <Tooltip content={REMOVED_RULE_TOOLTIP}>
              <span>
                <Icon glyph='fa-link-slash' size='xs' />
              </span>
            </Tooltip>
          </span>
        );
      }

      if (source.kind === 'plain' || onOpen === undefined) {
        return source.text;
      }

      const { link } = source;

      return (
        <Link
          icon={linkToPage ? 'fa-angle-right' : undefined}
          onClick={() => onOpen(link)}
          renderAs='button'
          type='button'
        >
          {linkToPage ? assignmentSourceLinkText(link) : source.text}
        </Link>
      );
    },
  });
